/**
 * Resolves the variant page a split-url flag payload routes the current request to.
 * Payload: { redirectTo: "/path", redirectFrom: "/sq", builderDomainId: "domain-aaa" } — the keys
 * keep their historical names because live flags use them; middleware.ts serves the target with a
 * rewrite. Applies only when builderDomainId matches this deployment, the current path equals
 * redirectFrom, and it is not already the target.
 */
import * as Sentry from '@sentry/nextjs';
import type {
	SplitUrlRedirectConfig,
	GetSplitUrlRedirectTargetParams,
} from './splitUrlRedirect.types';

export type {
	SplitUrlRedirectConfig,
	GetSplitUrlRedirectTargetParams,
} from './splitUrlRedirect.types';

/** Key prefix marking a flag as a split-URL test. */
const SPLIT_URL_FLAG_PREFIX = 'split-url-';

/** True for a flag key that names a split-URL test. */
export function isSplitUrlFlagKey(flagKey: string): boolean {
	return flagKey.startsWith(SPLIT_URL_FLAG_PREFIX);
}

/** Returns [key, value] entries for every split-URL flag, in key order. */
function getMatchingRedirectEntries(flagPayloads: Record<string, unknown>): [string, unknown][] {
	return Object.keys(flagPayloads)
		.filter(isSplitUrlFlagKey)
		.map((k) => [k, flagPayloads[k]]);
}

/** Normalizes a path (leading slash, no trailing slash for comparison). */
export function normalizePath(p: string): string {
	const t = p.trim();

	if (!t) return '/';

	const path = t.startsWith('/') ? t : `/${t}`;

	return path.replace(/\/$/, '') || '/';
}

/** Validates and normalizes a redirect target path (pathname only; query/hash stripped to avoid redirect loops). Returns null if invalid. */
function safeRedirectTo(value: unknown): string | null {
	if (typeof value !== 'string') return null;
	const trimmed = value.trim();
	if (trimmed.length === 0) return null;
	// Strip query and hash so targetPath is pathname-only; loop guard compares pathname to pathname.
	const pathnameOnly = trimmed.replace(/#.*$/, '').replace(/\?.*$/, '');
	if (pathnameOnly.length === 0) return null;
	if (pathnameOnly.startsWith('//') || pathnameOnly.includes('://')) return null;
	return pathnameOnly.startsWith('/') ? pathnameOnly : `/${pathnameOnly}`;
}

/** Parses payload; returns null if missing or any of redirectTo, redirectFrom, builderDomainId is missing/invalid. */
export function getRedirectConfigFromPayload(payload: unknown): SplitUrlRedirectConfig | null {
	if (!payload || typeof payload !== 'object') return null;

	const obj = payload as Record<string, unknown>;
	const targetPath = safeRedirectTo(obj['redirectTo']);
	if (!targetPath) return null;

	const rawBid = obj['builderDomainId'];
	let builderDomainId: string | undefined;
	if (typeof rawBid === 'string') {
		const trimmed = rawBid.trim();
		if (trimmed.length > 0) builderDomainId = trimmed;
	}
	if (!builderDomainId) return null;

	const rawFrom = obj['redirectFrom'];
	let sourcePath: string | undefined;
	if (typeof rawFrom === 'string') {
		const trimmed = rawFrom.trim();
		if (trimmed.length > 0) sourcePath = normalizePath(trimmed);
	}
	if (!sourcePath) return null;

	return { targetPath, sourcePath, builderDomainId };
}

/** Internal: runs redirect logic against an already-parsed config, avoiding double parsing. */
function getSplitUrlRedirectTargetFromConfig(
	config: SplitUrlRedirectConfig,
	params: GetSplitUrlRedirectTargetParams,
): string | null {
	const pub = params.publishedDomainId?.trim();
	if (!pub || config.builderDomainId !== pub) return null;

	const currentNormPath = normalizePath(params.currentPath || '/');
	if (currentNormPath !== config.sourcePath) return null;
	if (currentNormPath === normalizePath(config.targetPath)) return null;

	return config.targetPath + (params.search ?? '');
}

/** The variant page this request is served, and the split-url flag whose payload chose it. */
export type SplitUrlRewrite = { flagKey: string; targetPath: string };

/**
 * Tries each matching flag payload and returns the first rewrite that applies.
 * Reports to Sentry when more than one flag matches (same builderDomainId + redirectFrom) so config can be reviewed.
 */
export function getSplitUrlRewrite(
	flagPayloads: Record<string, unknown>,
	params: GetSplitUrlRedirectTargetParams,
): SplitUrlRewrite | null {
	if (!flagPayloads || typeof flagPayloads !== 'object' || params === null) return null;

	const matching = getMatchingRedirectEntries(flagPayloads).flatMap(([key, payload]) => {
		const config = getRedirectConfigFromPayload(payload);
		if (config === null) return [];

		const target = getSplitUrlRedirectTargetFromConfig(config, params);
		if (target === null) return [];

		return [{ key, target, redirectFrom: config.sourcePath }];
	});

	if (matching.length > 1) {
		// Use the payloads' configured redirectFrom (all match the same path when multiple collide).
		const redirectFrom = matching[0].redirectFrom;
		const builderDomainId = params.publishedDomainId?.trim();
		Sentry.captureMessage(
			'Multiple split-url redirect flags matched for same builderDomainId and redirectFrom',
			{
				level: 'warning',
				tags: { feature: 'split-url-redirect' },
				extra: {
					builderDomainId,
					redirectFrom,
					matchingFlagKeys: matching.map((m) => m.key),
				},
			},
		);
	}

	const [first] = matching;
	return first ? { flagKey: first.key, targetPath: first.target } : null;
}
